import { observer } from 'mobx-react'
import React, { forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'

import { Block, Checkbox, Heading } from 'suomifi-ui-components'
import {
  validateSpouseNotRelated,
  validateSpouseEmail,
  validateSpouseHetu,
} from 'todistukset-common/src/vtj/validation'
import LocalizedString, {
  LocalizedParagraphs,
} from 'todistukset-ui/src/vtj/components/localized-string/LocalizedString.react'

import {
  FormContainer,
  InfoInlineAlert,
  StyledTextInput,
} from 'todistukset-ui/src/vtj/components/order-wizard/CommonFormComponents.sc'
import type { StepComponent } from 'todistukset-ui/src/vtj/components/order-wizard/OrderWizardStep.type'
import {
  getErrorStatus,
  useFirstErrorImperativeHandle,
} from 'todistukset-ui/src/vtj/components/order-wizard/errors.util'
import { useAetOrdererFormActions } from 'todistukset-ui/src/vtj/stores/actions/aet-orderer-form-actions'
import { useAetOrdererForm } from 'todistukset-ui/src/vtj/stores/aet-orderer-form.store'
import { useStore } from 'todistukset-ui/src/vtj/stores/store.util'
import { TodistuksetUser } from 'todistukset-common/src/vtj/types/user'
import { normalizeTextInputWhitespace } from 'todistukset-common/src/vtj/form-sanitization.util'
import { AetOrdererUiForm } from 'todistukset-common/src/vtj/types/form-types.type'
import { finalEditableFieldValue } from 'todistukset-common/src/vtj/types/editable-field.type'

const AetSpouseInfoStep: StepComponent = observer(
  forwardRef(function AetSpouseInfoStep({ titleId, orderFlowName }, ref) {
    const { t } = useTranslation()

    const {
      common: { loggedInUser },
      steps: {
        [orderFlowName]: { showStepValidationErrors: showErrors },
      },
    } = useStore()

    const form = useAetOrdererForm()
    const { spouseHetu, sukulaisuussuhdeVakuutettu, spouseEmail } = form
    const { setSpouseHetu, setSukulaisuussuhdeVakuutettu, setSpouseEmail } =
      useAetOrdererFormActions()
    const user = loggedInUser.get()

    const refs = useFirstErrorImperativeHandle(ref, form, getErrors, [
      'spouseHetu',
      'notRelatedCheckbox',
      'spouseEmail',
    ])

    return (
      <FormContainer>
        <Block variant="section">
          <Heading variant="h2" id={titleId}>
            <LocalizedString stringKey="aetOrdererSpouseInfoMainTitle" />
          </Heading>
          <LocalizedParagraphs stringKey="aetOrdererSpouseInfoMainDescription_paragraphs" />
        </Block>
        <Block variant="section">
          <StyledTextInput
            id="spouseHetu"
            name="spouseHetu"
            labelText={t('aetSpouseInfoHetuLabel')}
            visualPlaceholder=""
            fullWidth
            type="text"
            value={spouseHetu}
            onChange={(value) =>
              setSpouseHetu(normalizeTextInputWhitespace(value))
            }
            forwardedRef={refs.spouseHetu}
            {...errorStatus({
              field: 'spouseHetu',
              form,
              user,
              showErrors,
              t,
            })}
          />
          <Checkbox
            mt="xl"
            mb="xxl"
            variant="small"
            checked={!!sukulaisuussuhdeVakuutettu.ilmoitettuArvo}
            onClick={setSukulaisuussuhdeVakuutettu}
            forwardedRef={refs.notRelatedCheckbox}
            {...errorStatus({
              field: 'notRelatedCheckbox',
              form,
              user,
              showErrors,
              t,
            })}
          >
            <LocalizedString stringKey="aetSpouseNotRelatedCheckboxLabel" />
          </Checkbox>
          <InfoInlineAlert aria-live="polite" mb="xxl">
            <LocalizedString stringKey="aetSpouseNotRelatedDescription_html" />
          </InfoInlineAlert>
        </Block>
        <Block variant="section">
          <Heading variant="h3">
            <LocalizedString stringKey="aetOrdererSpouseInfoContactTitle" />
          </Heading>
          <LocalizedParagraphs stringKey="aetOrdererSpouseInfoContactDescription_paragraphs" />
          <StyledTextInput
            mb="xxl"
            id="spouseEmail"
            name="spouseEmail"
            labelText={t('aetSpouseInfoEmailLabel')}
            visualPlaceholder=""
            fullWidth
            type="text"
            value={spouseEmail}
            onChange={(value) =>
              setSpouseEmail(normalizeTextInputWhitespace(value))
            }
            forwardedRef={refs.spouseEmail}
            {...errorStatus({
              field: 'spouseEmail',
              form,
              user,
              showErrors,
              t,
            })}
          />
        </Block>
      </FormContainer>
    )
  })
)

export default AetSpouseInfoStep

const errorStatus = (params: {
  field: keyof ReturnType<typeof getErrors>
  form: AetOrdererUiForm
  user: TodistuksetUser
  showErrors: boolean
  t: TFunction
}) => {
  return getErrorStatus({
    ...params,
    errors: {
      ...getErrors(params.form, params.user),
    },
  })
}

const getErrors = (form: AetOrdererUiForm, user: TodistuksetUser) => {
  return {
    spouseHetu: validateSpouseHetu(form.spouseHetu, form, user),
    notRelatedCheckbox: validateSpouseNotRelated(
      finalEditableFieldValue(form.sukulaisuussuhdeVakuutettu)
    ),
    spouseEmail: validateSpouseEmail(form.spouseEmail),
  }
}
